/**
 * F03.3-T10 Search analytics dispatcher.
 * API: window.PMA.analytics.track(eventName, payload)
 * Dispatches CustomEvent('pma:analytics', { detail }); optional dataLayer.push.
 * No PII; long terms trimmed (e.g. 100 chars).
 */
(function () {
  "use strict";

  var MAX_TERM_LENGTH = 100;

  function trimTerm(term) {
    if (term == null || typeof term !== "string") return "";
    var t = term.trim();
    return t.length > MAX_TERM_LENGTH ? t.slice(0, MAX_TERM_LENGTH) : t;
  }

  function sanitizePayload(payload) {
    if (payload == null || typeof payload !== "object") return {};
    var out = {};
    for (var key in payload) {
      if (!Object.prototype.hasOwnProperty.call(payload, key)) continue;
      var v = payload[key];
      if (key === "term" || key === "normalized" || key === "query") {
        out[key] = trimTerm(v);
      } else {
        out[key] = v;
      }
    }
    return out;
  }

  function isCanonicalEventName(eventName) {
    return typeof eventName === "string" && eventName.indexOf("pma_") === 0;
  }

  function track(eventName, payload, options) {
    if (!isCanonicalEventName(eventName)) return;
    var sanitized = sanitizePayload(payload || {});
    if (typeof window.PMAAnalytics !== "undefined" && typeof window.PMAAnalytics.track === "function") {
      window.PMAAnalytics.track(eventName, sanitized, options);
      return;
    }
    var detail = { event: eventName, payload: sanitized };
    try {
      document.dispatchEvent(new CustomEvent("pma:analytics", { detail: detail, bubbles: true }));
    } catch {}
    try {
      if (typeof window.dataLayer !== "undefined" && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: eventName, search: detail.payload });
      }
    } catch {}
  }

  function producerLinkClick(el) {
    var context = el.getAttribute("data-context") || "pdp";
    var destinationType = el.getAttribute("data-destination-type") || "profile_page";
    var producerHandle = el.closest("[data-producer-attribution]") && el.closest("[data-producer-attribution]").getAttribute("data-producer-handle") || "";
    track("pma_producer_card_click", {
      producer_handle: (producerHandle || "").slice(0, 100),
      source: destinationType === "directory_fallback" ? "directory" : context
    });
  }

  function initProducerAnalytics() {
    document.addEventListener(
      "click",
      function (e) {
        var link = e.target.closest && e.target.closest("[data-producer-link]");
        if (link) producerLinkClick(link);
      },
      false
    );

    // APA-UX-031 / GH-6803: record missing-producer QA follow-ups (no PII).
    try {
      var missing = document.querySelectorAll(
        "[data-producer-attribution][data-producer-mapping-missing], [data-producer-attribution][data-pma-qa-producer-followup='required'], [data-pma-qa-producer-followup='required']"
      );
      for (var i = 0; i < missing.length; i += 1) {
        var el = missing[i];
        if (el.getAttribute("data-producer-mapping-reported") === "true") continue;
        el.setAttribute("data-producer-mapping-reported", "true");
        var reason =
          el.getAttribute("data-producer-mapping-missing") ||
          (el.getAttribute("data-producer-source") === "association-fallback" ? "association_fallback" : "unresolved");
        track("pma_producer_mapping_missing", {
          product_handle: (el.getAttribute("data-product-handle") || "").slice(0, 100),
          vendor_value: (el.getAttribute("data-vendor-value") || "").slice(0, 100),
          has_producer_handle: el.getAttribute("data-has-producer-handle") === "true",
          reason: String(reason).slice(0, 60),
          context: (el.getAttribute("data-context") || "card").slice(0, 40),
          producer_source: (el.getAttribute("data-producer-source") || "").slice(0, 40)
        });
      }
    } catch (err) {
      /* ignore analytics failures */
    }

    // Impressions for governed attribution blocks (once per element).
    try {
      if (typeof IntersectionObserver === "function") {
        var seen = typeof WeakSet === "function" ? new WeakSet() : null;
        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              var node = entry.target;
              if (seen) {
                if (seen.has(node)) return;
                seen.add(node);
              } else if (node.getAttribute("data-producer-impression-reported") === "true") {
                return;
              }
              node.setAttribute("data-producer-impression-reported", "true");
              track("pma_producer_attribution_impression", {
                context: (node.getAttribute("data-context") || "card").slice(0, 40),
                product_handle: (node.getAttribute("data-product-handle") || "").slice(0, 100),
                producer_source: (node.getAttribute("data-producer-source") || "").slice(0, 40),
                producer_handle: (node.getAttribute("data-producer-handle") || "").slice(0, 100),
                producer_display_name: (node.getAttribute("data-producer-display-name") || "").slice(0, 120)
              });
              observer.unobserve(node);
            });
          },
          { threshold: 0.2 }
        );
        document.querySelectorAll("[data-producer-attribution]").forEach(function (node) {
          observer.observe(node);
        });
      }
    } catch (err2) {
      /* ignore */
    }
  }

  function getMemberState() {
    return document.body && document.body.classList.contains("is-member-active") ? "active" : "locked";
  }

  function initCartAnalytics() {
    var cartSection = document.querySelector("[data-section-handle=\"pma__commerce__cart_summary\"]");
    var cartItemsSection = document.querySelector("[data-section-handle=\"pma__commerce__cart_items\"]");
    if (!cartSection || !document.location.pathname.match(/^\/cart\/?$/)) return;
    var surface = cartSection.getAttribute("data-cart-surface") || "page";
    var itemCount = 0;
    if (cartItemsSection) {
      var n = parseInt(cartItemsSection.getAttribute("data-cart-item-count"), 10);
      if (!isNaN(n)) itemCount = n;
    }
    var isActiveMember = document.body && document.body.classList.contains("is-member-active");
    var memberState = getMemberState();
    var cartSummary = cartSection.querySelector(".pma-cart-summary");
    var cartBranch = cartSummary ? cartSummary.getAttribute("data-cart-branch") || memberState : memberState;
    track("pma_cart_view", {
      cart_surface: surface,
      item_count: itemCount,
      is_totals_locked: !isActiveMember,
      cart_branch: cartBranch
    });
    if (memberState === "locked") {
      track("pma_locked_cart_impression", {
        cart_surface: surface,
        cart_branch: cartBranch,
        member_state: memberState,
        totals_locked: true,
        item_count: itemCount
      });
      track("pma_cart_lock_impression", {
        lock_state: memberState,
        surface: surface,
        totals_locked: true,
        items_count: itemCount
      });
    }
    if (cartBranch === "membership_only") {
      track("pma_membership_activation_cart_view", {
        cart_surface: surface,
        cart_branch: cartBranch,
        checkout_context: "approved_inactive_activation",
        item_count: itemCount
      });
    }

    document.addEventListener("click", function (e) {
      if (!cartSection.contains(e.target)) return;
      var joinLink = e.target.closest && e.target.closest("a[data-pma-cta=\"join\"]");
      if (joinLink) {
        track("pma_cart_join_click", { surface: surface });
        track("pma_locked_cart_cta_click", {
          cart_surface: surface,
          cart_branch: cartBranch,
          member_state: getMemberState(),
          cta: "application",
          destination: "application_url"
        });
        track("pma_cart_lock_cta_click", {
          lock_state: getMemberState(),
          surface: surface,
          cta_type: "join",
          destination: "join_url"
        });
        return;
      }
      var loginLink = e.target.closest && e.target.closest("a[href*=\"account/login\"]");
      if (loginLink && !document.body.classList.contains("is-member-active")) {
        track("pma_locked_cart_cta_click", {
          cart_surface: surface,
          cart_branch: cartBranch,
          member_state: "locked",
          cta: "sign_in",
          destination: "login_url"
        });
        track("pma_cart_lock_cta_click", {
          lock_state: "locked",
          surface: surface,
          cta_type: "login",
          destination: "login_url"
        });
        return;
      }
      var checkoutBtn = e.target.closest && e.target.closest("button[name=\"checkout\"], .pma-member-only button[type=\"submit\"]");
      if (checkoutBtn) {
        var ms = getMemberState();
        if (ms === "active") {
          track("pma_checkout_start", {
            cart_surface: surface,
            cart_branch: cartBranch,
            checkout_context: "member_checkout"
          });
        } else if (cartBranch === "membership_only") {
          track("pma_membership_purchase_start", {
            plan_id: "approved_membership",
            checkout_context: "approved_inactive_activation"
          });
        }
        track("pma_cart_checkout_click", { surface: surface });
        track("pma_cart_checkout_attempt", {
          cart_surface: surface,
          result: ms === "active" ? "attempted" : "blocked",
          block_reason: ms !== "active" ? "membership_required" : undefined
        });
      }
    }, false);
  }

  function initAddToCartAttemptAnalytics() {
    // Emit pma_add_to_cart_attempt when a cart/add form is submitted (PDP + best-effort other surfaces).
    // Membership interest saves (#6817 / d9c607e699d9) emit pma_membership_interest_save instead of blocked ATC.
    // Required fields per schema: product_handle, quantity, result.
    document.addEventListener(
      "submit",
      function (e) {
        var form = e.target && e.target.nodeName === "FORM" ? e.target : null;
        if (!form) return;
        var action = (form.getAttribute && form.getAttribute("action")) ? String(form.getAttribute("action")) : "";
        if (action.indexOf("/cart/add") === -1 && action.indexOf("cart/add") === -1) return;

        var isMembershipInterest =
          (form.classList && form.classList.contains("pma-order-plan-form")) ||
          (form.querySelector && form.querySelector("[data-pma-membership-interest-save]")) ||
          (form.querySelector && form.querySelector("[data-pma-order-plan-save]")) ||
          false;

        var qty = 1;
        try {
          var q = form.querySelector && form.querySelector('input[name="quantity"]');
          if (q && q.value) {
            var n = parseInt(String(q.value), 10);
            if (!isNaN(n) && n > 0) qty = n;
          }
        } catch {}

        var variantId = "";
        try {
          var idInput = form.querySelector && form.querySelector('input[name="id"]');
          if (idInput && idInput.value) variantId = String(idInput.value).trim();
        } catch {}

        var productHandle = "";
        try {
          var handleEl =
            (form.querySelector && form.querySelector("[data-product-handle]")) ||
            (form.closest && form.closest("[data-product-handle]")) ||
            (form.closest && form.closest("[data-reco-tile][data-product-handle]")) ||
            null;
          if (handleEl) productHandle = handleEl.getAttribute("data-product-handle") || "";
        } catch {}

        if (!productHandle) {
          try {
            var ctx = typeof window.PMA_Analytics_Context === "object" ? window.PMA_Analytics_Context : {};
            if (ctx && ctx.resource_type === "product" && ctx.resource_handle) productHandle = String(ctx.resource_handle);
          } catch {}
        }

        if (!productHandle) {
          try {
            var m = (window.location.pathname || "").match(/\/products\/([^/?#]+)/);
            if (m) productHandle = m[1];
          } catch {}
        }

        if (!productHandle) return;

        var isActive = document.body && document.body.classList.contains("is-member-active");
        var surface = "unknown";
        try {
          var surfaceEl = form.querySelector && form.querySelector("[data-pma-order-plan-surface], [data-pma-cta=\"save_for_membership\"], [data-pma-cta=\"save_to_order_plan\"]");
          if (surfaceEl && surfaceEl.getAttribute("data-pma-order-plan-surface")) {
            surface = String(surfaceEl.getAttribute("data-pma-order-plan-surface"));
          } else if (form.getAttribute && form.getAttribute("data-pma-order-plan-surface")) {
            surface = String(form.getAttribute("data-pma-order-plan-surface"));
          }
        } catch {}

        if (isMembershipInterest && !isActive) {
          var memberState = "locked";
          try {
            if (document.body && document.body.getAttribute("data-pma-member-state")) {
              memberState = String(document.body.getAttribute("data-pma-member-state"));
            }
            var ui = form.querySelector && form.querySelector("[data-member-state]");
            if (ui && ui.getAttribute("data-member-state")) memberState = String(ui.getAttribute("data-member-state"));
          } catch {}
          var opPayload = {
            product_handle: String(productHandle).slice(0, 100),
            quantity: qty,
            surface: String(surface).slice(0, 40),
            member_state: String(memberState).slice(0, 40),
            result: "submitted"
          };
          if (variantId) opPayload.variant_id = variantId;
          track("pma_membership_interest_save", opPayload);
          return;
        }

        var sellingPlanVal = "";
        try {
          var spInput = form.querySelector && form.querySelector('input[name="selling_plan"]');
          if (spInput && spInput.value) sellingPlanVal = String(spInput.value).trim();
        } catch {}
        if (isActive && sellingPlanVal) {
          track("pma_order_plan_subscription_add", {
            product_handle: String(productHandle).slice(0, 100),
            quantity: qty,
            surface: String(surface).slice(0, 40),
            selling_plan_id: sellingPlanVal.slice(0, 40),
            result: "submitted"
          });
        }

        var payload = {
          product_handle: String(productHandle).slice(0, 100),
          quantity: qty,
          result: isActive ? "submitted" : "blocked"
        };
        if (variantId) payload.variant_id = variantId;
        if (!isActive) payload.block_reason = "membership_required";

        track("pma_add_to_cart_attempt", payload);
        if (isActive) {
          track("pma_active_add_to_cart", {
            product_handle: payload.product_handle,
            variant_id: payload.variant_id,
            quantity: payload.quantity
          });
        }
      },
      false
    );
  }

  function initProductCardAnalytics() {
    var QUICK_ADD_PENDING_KEY = "pma_product_card_quick_add_pending_v1";

    function cardPayload(card) {
      var section = card && card.closest ? card.closest("[data-section-handle]") : null;
      var productIdRaw = card ? card.getAttribute("data-product-id") || "" : "";
      var productId = productIdRaw ? parseInt(productIdRaw, 10) : undefined;
      return {
        product_handle: String(card ? card.getAttribute("data-product-handle") || "" : "").slice(0, 100),
        product_id: Number.isFinite(productId) ? productId : undefined,
        card_surface: section ? section.getAttribute("data-section-handle") || "unknown" : "unknown",
        member_state: card ? card.getAttribute("data-pma-card-member-state") || getMemberState() : getMemberState(),
        quick_add_state: card ? card.getAttribute("data-pma-card-quick-add") || "disabled" : "disabled"
      };
    }

    function quantityFromForm(form) {
      try {
        var q = form.querySelector && form.querySelector('input[name="quantity"]');
        if (q && q.value) {
          var n = parseInt(String(q.value), 10);
          if (!isNaN(n) && n > 0) return n;
        }
      } catch {}
      return 1;
    }

    try {
      if (window.location && window.location.pathname && window.location.pathname.match(/^\/cart\/?$/)) {
        var pendingRaw = sessionStorage.getItem(QUICK_ADD_PENDING_KEY);
        if (pendingRaw) {
          sessionStorage.removeItem(QUICK_ADD_PENDING_KEY);
          var pending = JSON.parse(pendingRaw);
          if (pending && pending.product_handle) {
            track("pma_product_card_quick_add_success", {
              product_handle: String(pending.product_handle).slice(0, 100),
              product_id: pending.product_id,
              card_surface: pending.card_surface || "unknown",
              quantity: pending.quantity || 1,
              result: "cart_reached"
            });
          }
        }
      }
    } catch {}

    document.addEventListener(
      "click",
      function (e) {
        var actionEl = e.target.closest && e.target.closest("[data-pma-card-action]");
        if (!actionEl) return;
        var card = actionEl.closest && actionEl.closest("[data-pma-product-card]");
        if (!card) return;
        var action = actionEl.getAttribute("data-pma-card-action") || "card_click";
        var payload = cardPayload(card);
        payload.action = action;
        if (action === "view_details") {
          track("pma_product_card_view_details_click", payload);
        } else if (action === "select_options") {
          track("pma_product_card_select_options_click", payload);
        } else {
          track("pma_product_card_click", payload);
        }
      },
      false
    );

    document.addEventListener(
      "submit",
      function (e) {
        var form = e.target && e.target.nodeName === "FORM" ? e.target : null;
        if (!form || !form.classList || !form.classList.contains("pma-product-card__form")) return;
        var card = form.closest && form.closest("[data-pma-product-card]");
        if (!card) return;
        var payload = cardPayload(card);
        payload.quantity = quantityFromForm(form);
        var isActive = document.body && document.body.classList.contains("is-member-active");
        var disabledSubmit = form.querySelector && form.querySelector('button[type="submit"][disabled]');
        if (!isActive || disabledSubmit) {
          payload.result = "blocked";
          payload.block_reason = !isActive ? "membership_required" : "product_unavailable";
          track("pma_product_card_quick_add_attempt", payload);
          if (!isActive) e.preventDefault();
          return;
        }
        payload.result = "submitted";
        track("pma_product_card_quick_add_attempt", payload);
        try {
          sessionStorage.setItem(QUICK_ADD_PENDING_KEY, JSON.stringify(payload));
        } catch {}
      },
      false
    );
  }

  function initProductMediaAnalytics() {
    document.addEventListener(
      "click",
      function (e) {
        var thumb = e.target.closest && e.target.closest("[data-pma-gallery-thumb]");
        if (!thumb) return;
        var section = thumb.closest("[data-product-handle]");
        var productHandle = section ? section.getAttribute("data-product-handle") || "" : "";
        var mediaIndex = thumb.getAttribute("data-media-index");
        var mediaType = thumb.getAttribute("data-media-type") || "image";
        track("pma_product_media_interact", {
          product_handle: productHandle,
          action: "select",
          media_index: mediaIndex !== null && mediaIndex !== undefined ? parseInt(mediaIndex, 10) : undefined,
          media_type: mediaType
        });
      },
      false
    );
  }

  function initContentExpandAnalytics() {
    document.addEventListener(
      "click",
      function (e) {
        var toggle = e.target.closest && e.target.closest("[data-accordion-toggle]");
        if (!toggle) return;
        var targetId = toggle.getAttribute("data-target");
        var panel = targetId ? document.getElementById(targetId) : null;
        if (!panel) return;
        var contentBlockId = targetId || (panel.id || "");
        setTimeout(function () {
          var action = panel.hidden ? "collapse" : "expand";
          track("pma_content_expand", { content_block_id: contentBlockId, action: action });
        }, 0);
      },
      false
    );
  }

  function initSearchResultsPageClickAnalytics() {
    var section = document.querySelector(".pma-section--search-results");
    if (!section) return;
    var query = section.getAttribute("data-pma-search-query") || "";
    document.addEventListener(
      "click",
      function (e) {
        var link = e.target.closest && e.target.closest("a[href]");
        if (!link || !section.contains(link)) return;
        var href = (link.getAttribute("href") || link.href || "").split("?")[0];
        var resultType = "";
        var handle = "";
        try {
          var path = new URL(href, window.location.origin).pathname;
          var parts = path.split("/").filter(Boolean);
          if (path.indexOf("/products/") !== -1) {
            resultType = "product";
            handle = parts[parts.length - 1] || "";
          } else if (path.indexOf("/blogs/") !== -1 && path.indexOf("/articles/") !== -1) {
            resultType = "article";
            handle = parts[parts.length - 1] || "";
          } else if (path.indexOf("/pages/") !== -1) {
            resultType = "page";
            handle = parts[parts.length - 1] || "";
          }
        } catch {}
        if (!resultType) return;
        var tabs = section.querySelectorAll(".pma-tab-panel");
        var allLinks = [];
        tabs.forEach(function (panel) {
          panel.querySelectorAll("a[href*='/products/'], a[href*='/blogs/'], a[href*='/pages/']").forEach(function (a) {
            allLinks.push(a);
          });
        });
        var idx = allLinks.indexOf(link);
        var position = idx >= 0 ? idx + 1 : 0;
        track("pma_search_result_click", {
          query_length: String(query || "").trim().slice(0, 200).length,
          result_type: resultType,
          result_position: position,
          resource_handle: handle
        });
      },
      false
    );
  }

  function initSearchToApplicationAnalytics() {
    var section = document.querySelector(".pma-section--search-results");
    if (!section) return;
    var searchText = section.getAttribute("data-pma-search-query") || "";
    document.addEventListener(
      "click",
      function (e) {
        var link = e.target.closest && e.target.closest("a[href]");
        if (!link || !section.contains(link)) return;
        var href = link.getAttribute("href") || "";
        var isApplicationCta = link.getAttribute("data-pma-cta") === "join" || href.indexOf("/pages/become-a-member") !== -1;
        if (!isApplicationCta) return;
        track("pma_search_to_application_click", {
          search_ui: "results_page",
          query_length: String(searchText || "").trim().slice(0, 200).length
        });
      },
      false
    );
  }

  function initPageContextAnalytics() {
    var MAX_LINK_TEXT = 100;
    function truncate(s) {
      if (s == null || typeof s !== "string") return "";
      var t = s.trim();
      return t.length > MAX_LINK_TEXT ? t.slice(0, MAX_LINK_TEXT) : t;
    }
    document.addEventListener(
      "click",
      function (e) {
        var el = e.target.closest && e.target.closest("[data-pma-nav-area]");
        if (!el || el.tagName !== "A") return;
        var navArea = el.getAttribute("data-pma-nav-area") || "primary";
        var linkText = truncate(el.textContent || "");
        var linkUrlPath = (el.getAttribute("href") && new URL(el.href, window.location.origin).pathname) || "";
        track("pma_nav_click", { nav_area: navArea, link_text: linkText, link_url_path: linkUrlPath });
      },
      false
    );
  }

  function initComponentViewAnalytics() {
    var seenKey = "pma_component_view_seen";
    var path = typeof window.location !== "undefined" ? window.location.pathname || "" : "";
    function getSeen() {
      try {
        var raw = sessionStorage.getItem(seenKey);
        return raw ? JSON.parse(raw) : {};
      } catch {
        return {};
      }
    }
    function markSeen(id) {
      try {
        var o = getSeen();
        if (!o[path]) o[path] = {};
        o[path][id] = true;
        sessionStorage.setItem(seenKey, JSON.stringify(o));
      } catch {}
    }
    function isSeen(id) {
      var o = getSeen();
      return o[path] && o[path][id];
    }
    var nodes = document.querySelectorAll("[data-pma-component-id]");
    if (nodes.length === 0) return;
    var observer =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                if (entry.intersectionRatio < 0.5) return;
                var el = entry.target;
                var id = el.getAttribute("data-pma-component-id");
                if (!id || isSeen(id)) return;
                markSeen(id);
                var payload = { component_id: id };
                var v = el.getAttribute("data-pma-component-variant");
                if (v) payload.component_variant = v;
                var p = el.getAttribute("data-pma-component-position");
                if (p) payload.position = parseInt(p, 10) || 0;
                track("pma_component_view", payload);
              });
            },
            { rootMargin: "0px", threshold: 0.5 }
          )
        : null;
    nodes.forEach(function (el) {
      if (observer) observer.observe(el);
      else {
        var id = el.getAttribute("data-pma-component-id");
        if (id && !isSeen(id)) {
          markSeen(id);
          track("pma_component_view", { component_id: id });
        }
      }
    });
  }

  function initSearchSubmitAnalytics() {
    function searchUiForForm(form) {
      if (form.closest && form.closest(".pma-header__search")) return "header";
      if (form.closest && form.closest(".pma-search-drawer")) return "mobile_drawer";
      if (form.closest && form.closest(".pma-shop-hub__search")) return "shop_hub";
      if (form.closest && form.closest(".pma-section--search-results")) return "results_page";
      return "search_form";
    }
    document.addEventListener(
      "submit",
      function (e) {
        var form = e.target && e.target.nodeName === "FORM" ? e.target : null;
        if (!form || !form.action || form.action.indexOf("search") === -1) return;
        var qInput = form.querySelector && form.querySelector('input[name="q"]');
        var searchText = qInput && qInput.value ? qInput.value.trim() : "";
        var typeInput = form.querySelector && form.querySelector('input[name="type"]');
        track("pma_search_submit", {
          search_ui: searchUiForForm(form),
          query_length: searchText.slice(0, 200).length,
          search_type: typeInput && typeInput.value ? String(typeInput.value).slice(0, 40) : "product"
        });
      },
      false
    );
  }

  function initSearchFilterAnalytics() {
    var section = document.querySelector(".pma-section--search-results");
    if (!section) return;
    var searchText = section.getAttribute("data-pma-search-query") || "";
    document.addEventListener(
      "submit",
      function (e) {
        var form = e.target && e.target.nodeName === "FORM" ? e.target : null;
        if (!form || !(form.matches && form.matches("[data-pma-search-filters]"))) return;
        var checkedCount = form.querySelectorAll('input[type="checkbox"]:checked').length;
        var rangeTouched = false;
        form.querySelectorAll('input[type="number"]').forEach(function (input) {
          if ((input.value || "").trim()) rangeTouched = true;
        });
        track("pma_search_filter_apply", {
          search_ui: "results_page",
          query_length: String(searchText || "").trim().slice(0, 200).length,
          selected_filter_count: checkedCount + (rangeTouched ? 1 : 0)
        });
      },
      false
    );
  }

  function initSearchResultsViewAnalytics() {
    var section = document.querySelector(".pma-section--search-results");
    if (!section) return;
    var performed = (section.getAttribute("data-pma-search-performed") || "") === "true";
    if (!performed) return;

    var query = section.getAttribute("data-pma-search-query") || "";
    var queryLength = String(query || "").trim().slice(0, 200).length;
    var countRaw = section.getAttribute("data-pma-search-results-count") || "0";
    var resultsCount = parseInt(countRaw, 10);
    if (!Number.isFinite(resultsCount)) resultsCount = 0;

    if (resultsCount === 0) {
      track("pma_search_no_results", { search_ui: "page", query_length: queryLength, results_count: 0 });
    } else {
      track("pma_search_results_view", { search_ui: "page", query_length: queryLength, results_count: resultsCount });
    }
  }

  function initServerRenderedEssentialEvents() {
    var els = document.querySelectorAll("[data-pma-analytics-event][data-pma-analytics-consent]");
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.getAttribute("data-pma-analytics-fired") === "true") continue;
      var eventName = (el.getAttribute("data-pma-analytics-event") || "").trim();
      var consentClass = (el.getAttribute("data-pma-analytics-consent") || "").trim() || "essential";
      if (!eventName) continue;

      var payload = {};
      var src = (el.getAttribute("data-pma-analytics-source") || "").trim();
      if (src) payload.source = src.slice(0, 120);
      var producerSlug = (el.getAttribute("data-pma-analytics-producer-slug") || "").trim();
      if (producerSlug) payload.producer_slug = producerSlug.slice(0, 80);

      try {
        if (typeof window.PMAAnalytics !== "undefined" && typeof window.PMAAnalytics.track === "function") {
          window.PMAAnalytics.track(eventName, payload, { consent_class: consentClass, once_per_page: true });
        } else {
          track(eventName, payload);
        }
        el.setAttribute("data-pma-analytics-fired", "true");
      } catch {
        // never block page rendering due to analytics errors
      }
    }
  }

  if (typeof window.PMA === "undefined") window.PMA = {};
  if (typeof window.PMA.analytics === "undefined") {
    window.PMA.analytics = { track: track };
  }
  function init() {
    initPageContextAnalytics();
    initComponentViewAnalytics();
    initProductMediaAnalytics();
    initContentExpandAnalytics();
    initSearchResultsPageClickAnalytics();
    initSearchToApplicationAnalytics();
    initSearchSubmitAnalytics();
    initSearchFilterAnalytics();
    initSearchResultsViewAnalytics();
    initProducerAnalytics();
    initCartAnalytics();
    initAddToCartAttemptAnalytics();
    initProductCardAnalytics();
    initServerRenderedEssentialEvents();
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
